import React, { useState, useEffect, useMemo } from 'react';
import { useFormBuilder } from '../../context/FormBuilderContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { 
  Search, ArrowLeft, Download, Trash2, Edit, Eye, RefreshCw,
  History, ChevronRight, Clock, User, Mail, BarChart3, X, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';

const ResponseList = ({ formId, onBack }) => {
  const { 
    responses, fetchResponses, fetchResponse, updateResponse, deleteResponse, 
    exportResponses, fetchForm, currentForm, loading 
  } = useFormBuilder();

  const [search, setSearch] = useState('');
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedAnswers, setEditedAnswers] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, response: null });
  
  // Bug 3: Filter state for choice field stats
  const [activeFilter, setActiveFilter] = useState(null); // { fieldId, value }

  useEffect(() => {
    if (formId) {
      fetchForm(formId);
      fetchResponses(formId);
    }
  }, [formId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchResponses(formId, { search: search || undefined });
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Bug 3: Calculate stats for choice fields (radio, checkbox)
  const choiceFieldStats = useMemo(() => {
    if (!currentForm?.fields || !responses.length) return {};
    
    const stats = {};
    
    currentForm.fields.forEach(field => {
      if (['radio', 'checkbox', 'dropdown', 'multiselect'].includes(field.type) && field.options?.length) {
        stats[field.id] = {
          field,
          options: {}
        };
        
        // Initialize all options with 0 count
        field.options.forEach(option => {
          stats[field.id].options[option] = 0;
        });
        
        // Count responses for each option
        responses.forEach(response => {
          const answer = response.answers?.[field.id];
          if (Array.isArray(answer)) {
            // Checkbox/multiselect - count each selected option
            answer.forEach(val => {
              if (stats[field.id].options[val] !== undefined) {
                stats[field.id].options[val]++;
              }
            });
          } else if (answer) {
            // Radio/dropdown - single value
            if (stats[field.id].options[answer] !== undefined) {
              stats[field.id].options[answer]++;
            }
          }
        });
      }
    });
    
    return stats;
  }, [currentForm, responses]);

  // Bug 3: Filter responses based on active filter
  const filteredResponses = useMemo(() => {
    if (!activeFilter) return responses;
    
    return responses.filter(response => {
      const answer = response.answers?.[activeFilter.fieldId];
      if (Array.isArray(answer)) {
        return answer.includes(activeFilter.value);
      }
      return answer === activeFilter.value;
    });
  }, [responses, activeFilter]);

  const handleViewResponse = async (response) => {
    const fullResponse = await fetchResponse(formId, response.id);
    setSelectedResponse(fullResponse || response);
    setEditedAnswers(fullResponse?.answers || response.answers || {});
    setEditMode(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedResponse) return;
    
    const result = await updateResponse(formId, selectedResponse.id, editedAnswers);
    if (result.success) {
      toast.success('Response updated');
      setSelectedResponse(result.data);
      setEditMode(false);
    } else {
      toast.error(result.error);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.response) return;
    
    const result = await deleteResponse(formId, deleteDialog.response.id);
    if (result.success) {
      toast.success('Response deleted');
      setDeleteDialog({ open: false, response: null });
      if (selectedResponse?.id === deleteDialog.response.id) {
        setSelectedResponse(null);
      }
    } else {
      toast.error(result.error);
    }
  };

  const handleExport = async () => {
    const data = await exportResponses(formId);
    if (!data) {
      toast.error('Failed to export responses');
      return;
    }

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.rows);
    XLSX.utils.book_append_sheet(wb, ws, 'Responses');
    
    // Download
    const filename = `${data.form_title || 'Form'}_Responses_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success('Export completed');
  };

  const getFieldLabel = (fieldId) => {
    if (!currentForm?.fields) return fieldId;
    const field = currentForm.fields.find(f => f.id === fieldId);
    return field?.label || fieldId;
  };

  const getCustomFields = () => {
    if (!currentForm?.fields) return [];
    return currentForm.fields.filter(f => !f.is_system_field);
  };

  const formatAnswer = (answer) => {
    if (answer === null || answer === undefined) return '-';
    if (Array.isArray(answer)) return answer.join(', ');
    return String(answer);
  };

  // Bug 3: Handle filter click
  const handleFilterClick = (fieldId, value) => {
    if (activeFilter?.fieldId === fieldId && activeFilter?.value === value) {
      setActiveFilter(null); // Clear filter if clicking same one
    } else {
      setActiveFilter({ fieldId, value });
    }
  };

  // Bug 3: Clear all filters
  const clearFilter = () => {
    setActiveFilter(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Responses</h2>
            <p className="text-slate-500">{currentForm?.title || 'Loading...'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} disabled={responses.length === 0}>
            <Download className="w-4 h-4 mr-2" /> Export to Excel
          </Button>
        </div>
      </div>

      {/* Bug 3: Response Stats for Choice Fields */}
      {Object.keys(choiceFieldStats).length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-orange-500" />
              Response Statistics
            </CardTitle>
            <CardDescription>
              Click on any option to filter responses
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {Object.entries(choiceFieldStats).map(([fieldId, data]) => (
              <div key={fieldId} className="space-y-2">
                <Label className="text-sm font-medium text-slate-700">
                  {data.field.label}
                </Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.options).map(([option, count]) => {
                    const isActive = activeFilter?.fieldId === fieldId && activeFilter?.value === option;
                    const percentage = responses.length > 0 
                      ? Math.round((count / responses.length) * 100) 
                      : 0;
                    
                    return (
                      <button
                        key={option}
                        onClick={() => handleFilterClick(fieldId, option)}
                        className={`
                          px-3 py-1.5 rounded-full text-sm font-medium transition-all
                          ${isActive 
                            ? 'bg-orange-500 text-white shadow-md' 
                            : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }
                        `}
                        data-testid={`stat-${fieldId}-${option}`}
                      >
                        {option}
                        <span className={`ml-2 ${isActive ? 'text-orange-100' : 'text-slate-500'}`}>
                          {count} ({percentage}%)
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Active Filter Indicator */}
      {activeFilter && (
        <div className="flex items-center gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
          <Filter className="w-4 h-4 text-orange-500" />
          <span className="text-sm text-orange-700">
            Filtering by: <strong>{getFieldLabel(activeFilter.fieldId)}</strong> = "{activeFilter.value}"
          </span>
          <span className="text-sm text-orange-500 ml-auto">
            ({filteredResponses.length} of {responses.length} responses)
          </span>
          <Button variant="ghost" size="sm" onClick={clearFilter} className="ml-2 h-7">
            <X className="w-4 h-4" />
            Clear
          </Button>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by email or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="response-search"
                />
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => fetchResponses(formId)}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Responses Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                {/* Bug 4: Add # column header */}
                <TableHead className="w-[60px]">#</TableHead>
                <TableHead>Respondent</TableHead>
                {getCustomFields().slice(0, 3).map(field => (
                  <TableHead key={field.id} className="hidden md:table-cell">
                    {field.label}
                  </TableHead>
                ))}
                <TableHead>Submitted</TableHead>
                <TableHead>Version</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : filteredResponses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {activeFilter ? 'No responses match the current filter' : 'No responses yet'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredResponses.map((response, index) => (
                  <TableRow 
                    key={response.id} 
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => handleViewResponse(response)}
                  >
                    {/* Bug 4: Add row number */}
                    <TableCell className="font-medium text-slate-500">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{response.name}</p>
                        <p className="text-sm text-slate-500">{response.email}</p>
                      </div>
                    </TableCell>
                    {getCustomFields().slice(0, 3).map(field => (
                      <TableCell key={field.id} className="hidden md:table-cell">
                        <span className="text-sm line-clamp-1">
                          {formatAnswer(response.answers?.[field.id])}
                        </span>
                      </TableCell>
                    ))}
                    <TableCell>
                      <span className="text-sm text-slate-500">
                        {response.submitted_at 
                          ? new Date(response.submitted_at).toLocaleDateString()
                          : '-'
                        }
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">v{response.version || 1}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={(e) => { e.stopPropagation(); handleViewResponse(response); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          className="text-red-500"
                          onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, response }); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Response Detail Sheet */}
      <Sheet open={!!selectedResponse} onOpenChange={(open) => !open && setSelectedResponse(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Response Details</SheetTitle>
            <SheetDescription>
              View and edit response data
            </SheetDescription>
          </SheetHeader>
          
          {selectedResponse && (
            <div className="mt-6 space-y-6">
              {/* Respondent Info */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedResponse.name}</p>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Mail className="w-3 h-3" /> {selectedResponse.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {selectedResponse.submitted_at 
                      ? new Date(selectedResponse.submitted_at).toLocaleString()
                      : 'Unknown'
                    }
                  </span>
                  <Badge variant="outline">Version {selectedResponse.version || 1}</Badge>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <Button onClick={handleSaveEdit} className="flex-1">
                      Save Changes
                    </Button>
                    <Button variant="outline" onClick={() => {
                      setEditMode(false);
                      setEditedAnswers(selectedResponse.answers || {});
                    }}>
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" onClick={() => setEditMode(true)} className="flex-1">
                      <Edit className="w-4 h-4 mr-2" /> Edit
                    </Button>
                    {(selectedResponse.history?.length > 0) && (
                      <Button variant="outline" onClick={() => setShowHistory(true)}>
                        <History className="w-4 h-4 mr-2" /> History
                      </Button>
                    )}
                  </>
                )}
              </div>

              <Separator />

              {/* Answers */}
              <div className="space-y-4">
                <h4 className="font-medium">Responses</h4>
                {getCustomFields().map(field => (
                  <div key={field.id} className="space-y-1">
                    <Label className="text-sm text-slate-500">{field.label}</Label>
                    {editMode ? (
                      field.type === 'paragraph' ? (
                        <textarea
                          className="w-full p-2 border rounded-lg text-sm"
                          rows={3}
                          value={editedAnswers[field.id] || ''}
                          onChange={(e) => setEditedAnswers(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                        />
                      ) : field.type === 'rating' ? (
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <button
                              key={star}
                              onClick={() => setEditedAnswers(prev => ({
                                ...prev,
                                [field.id]: star
                              }))}
                              className={`text-2xl ${
                                star <= (editedAnswers[field.id] || 0) 
                                  ? 'text-amber-400' 
                                  : 'text-slate-200'
                              }`}
                            >
                              ★
                            </button>
                          ))}
                        </div>
                      ) : (
                        <Input
                          type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                          value={editedAnswers[field.id] || ''}
                          onChange={(e) => setEditedAnswers(prev => ({
                            ...prev,
                            [field.id]: e.target.value
                          }))}
                        />
                      )
                    ) : (
                      <p className="text-sm p-2 bg-slate-50 rounded-lg">
                        {field.type === 'rating' ? (
                          <span className="text-amber-400 text-lg">
                            {'★'.repeat(selectedResponse.answers?.[field.id] || 0)}
                            {'☆'.repeat(5 - (selectedResponse.answers?.[field.id] || 0))}
                          </span>
                        ) : (
                          formatAnswer(selectedResponse.answers?.[field.id])
                        )}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              Previous submissions from {selectedResponse?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Current Version */}
            <div className="p-3 border rounded-lg bg-green-50 border-green-200">
              <div className="flex items-center justify-between mb-2">
                <Badge className="bg-green-100 text-green-700">Current - v{selectedResponse?.version || 1}</Badge>
                <span className="text-xs text-slate-500">
                  {selectedResponse?.submitted_at 
                    ? new Date(selectedResponse.submitted_at).toLocaleString()
                    : 'Unknown'
                  }
                </span>
              </div>
            </div>

            {/* Previous Versions */}
            {(selectedResponse?.history || []).slice().reverse().map((entry, index) => (
              <div key={index} className="p-3 border rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <Badge variant="outline">Version {entry.version}</Badge>
                  <span className="text-xs text-slate-500">
                    {entry.submitted_at 
                      ? new Date(entry.submitted_at).toLocaleString()
                      : 'Unknown'
                    }
                  </span>
                </div>
                <div className="space-y-2 mt-3">
                  {getCustomFields().slice(0, 5).map(field => (
                    <div key={field.id} className="text-sm">
                      <span className="text-slate-500">{field.label}: </span>
                      <span>{formatAnswer(entry.answers?.[field.id])}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => !open && setDeleteDialog({ open: false, response: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-destructive">Delete Response</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the response from "{deleteDialog.response?.email}"? 
              This will also delete all version history. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, response: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResponseList;
